import { useOutletContext } from 'react-router-dom'

import { CompensationsExpensesReimbursementsDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import MapExpenseCategoryWithPayrollConcept from 'modules/compensations/components/MapExpenseCategoryWithPayrollConcept'
import useUpsertReimbursablePayrollConcept from 'modules/compensations/hooks/useUpsertReimbursablePayrollConcept'
import { ReimbursableTypeEnum } from 'modules/compensations/lib/constants'

const ExpensesReimbursements = () => {
  const { company } = useCurrentSession()
  const { compensationsEnabled } = useOutletContext<{ compensationsEnabled: boolean }>()
  const { upsert: upsertCompensationMapping } = useUpsertReimbursablePayrollConcept()

  const { data, loading } = useQuery(CompensationsExpensesReimbursementsDocument, {
    variables: { companyId: company.id },
  })

  if (loading) return <Loading />

  if (!compensationsEnabled) return <NotFound />

  const reimbursements = data?.compensations.reimbursablePayrollConceptsConnection.nodes ?? []
  const payrollConcepts = data?.payroll.conceptsConnection.nodes ?? []
  const expensesCategories = data?.finance.customCategoriesConnection.nodes ?? []

  const onRowChanged = async (expenseCategoryId: string, payrollConceptId: string) => {
    const reimbursement = reimbursements.find((r) => r.reimbursableId === expenseCategoryId)

    const result = await upsertCompensationMapping({
      id: reimbursement?.id,
      payrollConceptId: payrollConceptId,
      reimbursableId: reimbursement ? reimbursement.reimbursableId : expenseCategoryId,
      reimbursableType: reimbursement
        ? reimbursement.reimbursableType
        : ReimbursableTypeEnum.FinanceCustomCategory,
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
  }

  return (
    <MapExpenseCategoryWithPayrollConcept
      reimbursements={reimbursements}
      payrollConcepts={payrollConcepts}
      expensesCategories={expensesCategories}
      onRowChanged={onRowChanged}
    />
  )
}

export default ExpensesReimbursements
